import { useQueryClient } from "@tanstack/react-query";
import { FormikProps, useFormik } from "formik";
import { useEffect, useMemo } from "react";
import type { Option } from "@/constants/common";
import { useSnackbar } from "@/hooks/useSnackbar";
import { GROUP_SESSION_LIST_QUERY_KEY } from "@/queries/group-visit/use-group-sessions";
import { useGroupVisitById } from "@/queries/group-visit/use-group-visit-by-id";
import { useGroupVisitList } from "@/queries/group-visit/use-group-visit-list";
import { useScheduleGroupSession } from "@/queries/group-visit/use-schedule-group-session";
import { GetAllGroupVisitsGroupStatusEnum } from "@/services";
import {
  DEF_PAGINATION,
  getErrorSnackbar,
  getSuccessSnackbar,
} from "@/utils/utils";
import {
  GROUP_FIELDS_INITIAL_VALUES,
  GROUP_FIELDS_SCHEMA,
} from "../group-visit-constant";
import { GroupDetail, GroupFieldsValues } from "../group-visit-types";
import {
  toGroupDetailFromGroupVisit,
  toGroupFieldsValues,
  toGroupSessionPayload,
} from "../group-visit-utils";

export type UseScheduleGroupAppointment = {
  formik: FormikProps<GroupFieldsValues>;
  groupOptions: Option[];
  isGroupsLoading: boolean;
  isGroupDetailLoading: boolean;
  /** Chosen group's record, for the read-only care team. */
  selectedGroup?: GroupDetail;
};

/**
 * Group side of the Schedule Appointment drawer: the group fields form, the
 * active groups it can pick from, the prefill from the chosen group's record,
 * and the schedule-session call on submit.
 */
export const useScheduleGroupAppointment = (
  onScheduled: () => void
): UseScheduleGroupAppointment => {
  const client = useQueryClient();
  const showSnackbar = useSnackbar();
  const { mutateAsync: scheduleSession } = useScheduleGroupSession();

  const { data, isLoading } = useGroupVisitList({
    pagination: DEF_PAGINATION,
    groupStatus: GetAllGroupVisitsGroupStatusEnum.Active,
    archive: false,
  });

  const groupOptions = useMemo<Option[]>(
    () =>
      (data?.data?.content ?? [])
        .filter((group) => !!group.uuid)
        .map((group) => ({
          value: group.uuid as string,
          label: group.groupName ?? "",
        })),
    [data]
  );

  const formik = useFormik<GroupFieldsValues>({
    initialValues: GROUP_FIELDS_INITIAL_VALUES,
    validationSchema: GROUP_FIELDS_SCHEMA,
    onSubmit: async (values, helpers) => {
      try {
        const res = await scheduleSession(toGroupSessionPayload(values));
        showSnackbar(getSuccessSnackbar(res));
        client.invalidateQueries({ queryKey: [GROUP_SESSION_LIST_QUERY_KEY] });
        client.invalidateQueries({ queryKey: ["appointment-list-v2"] });
        helpers.resetForm();
        onScheduled();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const { groupId } = formik.values;
  const { setValues, resetForm } = formik;
  const { data: groupDetail, isFetching: isGroupDetailLoading } =
    useGroupVisitById(groupId ?? undefined);

  useEffect(() => {
    if (!groupId) return;
    if (groupDetail?.uuid !== groupId) return;
    setValues(toGroupFieldsValues(groupDetail, groupId));
  }, [groupId, groupDetail, setValues]);

  useEffect(() => {
    if (!groupId) resetForm();
  }, [groupId, resetForm]);

  const selectedGroup = useMemo(
    () =>
      groupDetail && groupDetail.uuid === groupId
        ? toGroupDetailFromGroupVisit(groupDetail)
        : undefined,
    [groupDetail, groupId]
  );

  return {
    formik,
    groupOptions,
    isGroupsLoading: isLoading,
    isGroupDetailLoading,
    selectedGroup,
  };
};
